// frontend/src/features/ups/modals/MoveSiteModal.js
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompanySites,
  selectConcreteSelectedSiteId,
} from '../../sites/sitesSlice';
import { moveAssignment } from '../../../services/assignmentService';
import { loadSiteAssignments } from '../../assignments/assignmentsSlice';
import { bumpStatsRefreshKey } from '../../ui/filtersSlice';

const inputClass =
  'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20';
const labelClass = 'mb-1 block text-sm font-medium text-app-muted';
const primaryButtonClass =
  'rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClass =
  'rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60';

export default function MoveSiteModal({
  driverData,
  assignmentContext,
  onClose,
  onMoveSuccess,
  refreshAssignmentsAfterMove = true,
}) {
  const dispatch = useDispatch();

  const companySites = useSelector(selectCompanySites);
  // concrete fallback only; ALL_SITES must never be used as a write site.
  const selectedSiteId = useSelector(selectConcreteSelectedSiteId);

  const [targetSiteId, setTargetSiteId] = useState('');

  const [targetStatus, setTargetStatus] = useState(
    String(assignmentContext?.assignment_status || 'confirmed').toLowerCase()
  );
  const [isBusy, setIsBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const assignmentId = assignmentContext?.assignment_id ?? null;
  // Move writes must use the assignment's current site, not the global selector.
  const fromSiteId = assignmentContext?.site_id ?? driverData?.site_id ?? selectedSiteId ?? null;

  const fromSiteName =
    assignmentContext?.site_name ||
    (fromSiteId ? `Site #${fromSiteId}` : null);

  const currentStatus = String(assignmentContext?.assignment_status || '').toLowerCase();

  const availableSites = useMemo(() => {
    const currentId = Number(fromSiteId);
    return (companySites || []).filter((site) => Number(site?.id) !== currentId);
  }, [companySites, fromSiteId]);

  const safe = (v) => v ?? '—';

  const canSubmit =
    !!assignmentId &&
    !!fromSiteId &&
    !!targetSiteId &&
    Number(targetSiteId) !== Number(fromSiteId) &&
    !isBusy;

  async function handleSubmit(e) {
    e.preventDefault();

    if (!assignmentId) {
      setErrorMsg('No assignment is available to move.');
      return;
    }
    if (!fromSiteId) {
      setErrorMsg('Current site context is missing.');
      return;
    }
    if (!targetSiteId) {
      setErrorMsg('Please choose a target site.');
      return;
    }
    if (Number(targetSiteId) === Number(fromSiteId)) {
      setErrorMsg('Choose a different site.');
      return;
    }

    try {
      setIsBusy(true);
      setErrorMsg('');

      await moveAssignment(fromSiteId, assignmentId, {
        target_site_id: Number(targetSiteId),
        target_status: targetStatus || currentStatus || 'confirmed',
      });

      if (refreshAssignmentsAfterMove) {
        // Phase 5: do not force selectedSiteId; thunk handles single-site vs all-sites mode.
        await dispatch(
          loadSiteAssignments({
            statuses: ['requested', 'confirmed', 'active'],
          })
        );
      }

      dispatch(bumpStatsRefreshKey());
      onMoveSuccess?.();
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not move the assignment to the selected site.'
      );
    } finally {
      setIsBusy(false);
    }
  }

  const close = () => {
    if (isBusy) return;
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={close}
    >
      <div
        className="flex h-[78dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-site-title"
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
          <h2 id="move-site-title" className="text-xl font-semibold text-app-text">
            Move Site
          </h2>
          <button
            type="button"
            onClick={close}
            disabled={isBusy}
            aria-label="Close move site modal"
            className="rounded-xl px-3 py-2 text-xl font-bold text-app-muted transition hover:bg-app-surface hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
          >
            &times;
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {errorMsg && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errorMsg}
            </div>
          )}

          <div className="space-y-4 text-sm">
            <div className="space-y-1 rounded-xl border border-app-border bg-app-surface p-4 text-app-text">
              <p><strong>Driver:</strong> {safe(driverData?.name)}</p>
              <p>
                <strong>Current Status:</strong>{' '}
                <span className="capitalize">{safe(assignmentContext?.assignment_status)}</span>
              </p>
              <p><strong>Current Site:</strong> {safe(fromSiteName)}</p>
              <p><strong>Assignment ID:</strong> {safe(assignmentId)}</p>
            </div>

            <form id="move-site-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="move-site-target-site" className={labelClass}>
                  Move To Site
                </label>
                <select
                  id="move-site-target-site"
                  name="targetSiteId"
                  value={targetSiteId}
                  onChange={(e) => setTargetSiteId(e.target.value)}
                  disabled={isBusy}
                  className={inputClass}
                >
                  <option value="">Select a site</option>
                  {availableSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name || site.site_name || `Site #${site.id}`}
                    </option>
                  ))}
                </select>

                {availableSites.length === 0 && (
                  <p className="mt-2 text-xs text-app-muted">
                    No other company sites are available for this move.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="move-site-target-status" className={labelClass}>
                  Status After Move
                </label>
                <select
                  id="move-site-target-status"
                  name="targetStatus"
                  value={targetStatus}
                  onChange={(e) => setTargetStatus(e.target.value)}
                  disabled={isBusy}
                  className={inputClass}
                >
                  <option value="requested">Requested</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="active">Active</option>
                </select>
              </div>

              <div className="rounded-xl border border-app-border bg-app-surface p-3 text-xs text-app-muted">
                This moves the current open assignment to another site using the existing UPS move-site backend flow.
              </div>
            </form>
          </div>
        </div>

        <div
          className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={close}
            disabled={isBusy}
            className={secondaryButtonClass}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="move-site-form"
            disabled={!canSubmit}
            className={primaryButtonClass}
          >
            {isBusy ? 'Moving…' : 'Confirm Move'}
          </button>
        </div>
      </div>
    </div>
  );
}
